import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import Divider from "@mui/material/Divider";
import { styled } from "@mui/material/styles";
import { black, grey } from "designSystem/styles/color";
import { Box, Button, GridRow, IconButton, MenuItem, OverflowText, Text } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import ButtonMenu from "designSystem/components/menu/ButtonMenu";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, ReactNode, useRef } from "react";
import ColorPickerBox from "./ColorPickerBox";
import { useLightboxStore, zoomOptions, ZOOM_OPTION_GROUP_FIT, ZOOM_OPTION_GROUP_NUMBER } from "./LightboxStore";

const HeaderBox = styled(Box)(({ theme }) => ({
	alignItems: "center",
	backgroundColor: black[50],
	color: grey[400],
	display: "flex",
	flexShrink: 0,
	justifyContent: "space-between",
	paddingBottom: theme.spacing(2),
	paddingLeft: theme.spacing(3),
	paddingRight: theme.spacing(3),
	paddingTop: theme.spacing(2),
}));

const HeaderLeftBox = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	width: "45%",
}));

const HeaderCenterBox = styled(Box)(({ theme }) => ({
	flexShrink: 0,
	paddingLeft: theme.spacing(3),
	paddingRight: theme.spacing(3),
	width: "10%",
}));

const HeaderRight = styled(GridRow)(() => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "flex-end",
	width: "45%",
}));

export const LBHeader: FunctionComponent<{}> = observer(function LBHeader() {
	const {
		canEditImageFile,
		currentImage,
		downloadImage,
		getLocalizedImageLabels,
		index,
		numImages,
		onClose,
		onEditImage,
		selectZoomOption,
		showEditButton,
		toggleInfoPanel,
		scalePercentage,
		zoomDropDownSelected,
	} = useLightboxStore();

	const menuRef = useRef(null);
	const loc = useLocale();
	const { getNameOrUnnamed, resource } = loc;
	const imageLabels = getLocalizedImageLabels(loc);
	const imageTitleText = `${getNameOrUnnamed(currentImage.data.name)}${imageLabels?.length ? ` - ${imageLabels.join(", ")}` : ""}`;

	const createSection = (sectionName: string): ReactNode => (
		zoomOptions
			.filter((x) => x.group === sectionName)
			.map(({ id, name, value }) => (
				<MenuItem
					key={id}
					onClick={(e) => {
						selectZoomOption(id, value);
					}}
					selected={id === zoomDropDownSelected}
					text={resource(name) || name}
				/>
			))
	);

	const zoomResLabel = loc.resource("Zoom");

	return (
		<HeaderBox>
			<HeaderLeftBox>
				<OverflowText
					semiBold
					text={imageTitleText}
					variant="h5"
				/>
			</HeaderLeftBox>
			<HeaderCenterBox
				centerContent
				flexShrink={0}
				px={3}
			>
				<Text res="ProgressIndicator" resParams={[index + 1, numImages]} />
			</HeaderCenterBox>
			<HeaderRight spacing={3}>
				<ColorPickerBox />
				<IconButton
					aria-label={loc.resource("Information")}
					design="darkMode"
					onClick={toggleInfoPanel}
					res="Information"
				>
					<InfoIcon />
				</IconButton>
				<IconButton
					aria-label={loc.resource("Download")}
					design="darkMode"
					onClick={downloadImage}
					res="Download"
				>
					<DownloadIcon />
				</IconButton>
				<ButtonMenu
					aria-label={zoomResLabel}
					design="darkMode"
					endIcon={(
						<ArrowDropDownIcon
							style={{
								height: 16,
								width: 16,
							}}
						/>
					)}
					label={(
						<Text
							onClick={() => {
								menuRef?.current.click();
							}}
							text={`${scalePercentage}%`}
							width={35}
						/>
					)}
					ref={menuRef}
					title={zoomResLabel}
				>
					{createSection(ZOOM_OPTION_GROUP_NUMBER)}
					<Divider />
					{createSection(ZOOM_OPTION_GROUP_FIT)}
				</ButtonMenu>
				{showEditButton && (
					<Button
						aria-label={loc.resource("Edit")}
						design="darkModePrimary"
						disabled={!canEditImageFile}
						onClick={onEditImage}
						res="Edit"
					/>
				)}
				<IconButton
					aria-label={loc.resource("Close")}
					design="darkMode"
					onClick={onClose}
					res="Close"
				>
					<ClearIcon />
				</IconButton>
			</HeaderRight>
		</HeaderBox>
	);
});

export default LBHeader;
